import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';

const AllLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    action: '',
  });

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.action) params.append('action', filters.action);
      params.append('limit', '100');

      const response = await api.get(`/logs?${params.toString()}`);
      console.log('Logs reçus:', response.data); // Debug
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des logs:', error);
      setError('Erreur lors du chargement des logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
      ACTIVATE: 'bg-green-100 text-green-800',
      DEACTIVATE: 'bg-orange-100 text-orange-800',
      LOGIN: 'bg-purple-100 text-purple-800',
      LOGOUT: 'bg-gray-100 text-gray-800',
      PASSWORD_CHANGE: 'bg-yellow-100 text-yellow-800',
      PASSWORD_RESET: 'bg-yellow-100 text-yellow-800',
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-blue-100 text-blue-800',
      user_owner: 'bg-green-100 text-green-800',
      user_simple: 'bg-purple-100 text-purple-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        Journal d'activités (Logs)
      </h1>

      {/* Filtres */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par rôle
            </label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="">Tous les rôles</option>
              <option value="admin">Admin</option>
              <option value="user_owner">User Owner</option>
              <option value="user_simple">User Simple</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par action
            </label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="">Toutes les actions</option>
              <option value="CREATE">Création</option>
              <option value="UPDATE">Modification</option>
              <option value="DELETE">Suppression</option>
              <option value="LOGIN">Connexion</option>
              <option value="ACTIVATE">Activation</option>
              <option value="DEACTIVATE">Désactivation</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ role: '', action: '' });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
            >
              Réinitialiser les filtres
            </button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Liste des logs */}
      <Card>
        <div className="space-y-3">
          {logs.length > 0 ? (
            logs.map((log) => (
              <div
                key={log._id}
                className="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded ${getRoleBadgeColor(
                        log.actorRole
                      )}`}
                    >
                      {log.actorRole}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded ${getActionBadgeColor(
                        log.action
                      )}`}
                    >
                      {log.action}
                    </span>
                  </div>
                  <p className="font-semibold text-gray-800">{log.actorName}</p>
                  <p className="text-sm text-gray-600">{log.details}</p>
                  {log.targetName && (
                    <p className="text-xs text-gray-500 mt-1">
                      Cible: {log.targetName}
                    </p>
                  )}
                </div>
                <div className="text-right ml-4">
                  <p className="text-sm text-gray-500">
                    {new Date(log.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(log.createdAt).toLocaleTimeString('fr-FR')}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 py-8">
              {filters.role || filters.action
                ? 'Aucun log trouvé avec ces filtres'
                : 'Aucun log disponible. Les logs apparaîtront ici après des actions.'}
            </p>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Total: {logs.length} log(s)
        </div>
      </Card>
    </div>
  );
};

export default AllLogs;